/**
 * @sahaj/api-client
 *
 * Typed Axios API client for Sahaj Connect.
 * Handles base URL, authentication, token refresh, and error normalization.
 * All platforms (Web, Desktop, Mobile) use this package.
 */

import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

import { config } from '@sahaj/config';

import type {
  AuthResponse,
  AuthTokens,
  Channel,
  ChannelMember,
  CreateChannelRequest,
  CreateDirectConversationRequest,
  CreateGroupRequest,
  CreateInvitationRequest,
  CreateWorkspaceRequest,
  DirectConversation,
  DirectMessage,
  Group,
  GroupMember,
  LogoutRequest,
  LoginRequest,
  ActivateAccountRequest,
  ChangePasswordRequest,
  Message,
  Notification,
  PaginatedResponse,
  PageResponse,
  RefreshRequest,
  RegisterDeviceRequest,
  RegisterRequest,
  SendDirectMessageRequest,
  SendMessageRequest,
  UpdateChannelRequest,
  UpdateDeviceTokenRequest,
  UpdateGroupRequest,
  UpdateMessageRequest,
  UpdateWorkspaceRequest,
  UpdateUserProfileRequest,
  PlatformUser,
  User,
  Workspace,
  WorkspaceInvitation,
  WorkspaceMember,
  AcceptInvitationRequest,
  Device,
  WorkspaceFile,

  // Emergency Alert types
  CreateEmergencyAlertRequest,
  EmergencyAlertDto,

  // Platform types
  DashboardStatsDto,
  PlatformAuditLogDto,
  WorkspaceStatus,

  // Notification types
  NotificationFilterQuery,
  UserNotificationPreferences,
  UpdateNotificationPreferencesRequest,
  ChannelNotificationSetting,
  UpdateChannelNotificationSettingRequest,

  // Meetings types
  Meeting,
  MeetingStatusCounts,
  MeetingParticipant,
  MeetingInvitation,
  MeetingJoinTokenResponse,
  CreateMeetingRequest,
  UpdateMeetingRequest,
  AddParticipantRequest,
  CreateMeetingInvitationRequest,
  GuestJoinRequest,
  GuestJoinResponse,
  // Translation types
  TranslateRequest,
  TranslateResponse,
} from '@sahaj/types';

// ─── Token storage interface ───────────────────────────────────────────────────

/**
 * Platform-specific token storage adapter.
 *
 * Web: memory + HttpOnly cookie
 * Desktop: Tauri Stronghold
 * Mobile: expo-secure-store
 *
 * Implementors provide getToken/setToken/clearToken.
 * The api-client uses this interface — never touches storage directly.
 */
export interface TokenStorage {
  getAccessToken(): Promise<string | null>;
  setAccessToken(token: string): Promise<void>;
  getRefreshToken(): Promise<string | null>;
  setRefreshToken(token: string): Promise<void>;
  clearTokens(): Promise<void>;
}

// ─── Default in-memory storage (Web fallback — upgrade per platform) ──────────

class InMemoryTokenStorage implements TokenStorage {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;

  async getAccessToken() {
    return this.accessToken;
  }

  async setAccessToken(token: string) {
    this.accessToken = token;
  }

  async getRefreshToken() {
    return this.refreshToken;
  }

  async setRefreshToken(token: string) {
    this.refreshToken = token;
  }

  async clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
  }
}

// ─── API client factory ───────────────────────────────────────────────────────

export interface ApiClientOptions {
  storage?: TokenStorage;
  onUnauthorized?: () => void;
}

export function createApiClient(
  options: ApiClientOptions = {}
): SahajApiClient {
  return new SahajApiClient(options);
}

// ─── SahajApiClient ───────────────────────────────────────────────────────────

export class SahajApiClient {
  private readonly http: AxiosInstance;
  private readonly storage: TokenStorage;
  private readonly onUnauthorized: (() => void) | undefined;
  private refreshPromise: Promise<AuthTokens> | null = null;

  constructor(options: ApiClientOptions) {
    this.storage = options.storage ?? new InMemoryTokenStorage();
    this.onUnauthorized = options.onUnauthorized;

    this.http = axios.create({
      baseURL: config.api.baseUrl,
      timeout: 15_000,
      headers: { 'Content-Type': 'application/json' },
    });

    this.http.interceptors.request.use(
      this.requestInterceptor.bind(this)
    );

    this.http.interceptors.response.use(
      undefined,
      this.responseErrorInterceptor.bind(this)
    );
  }

  private async requestInterceptor(
    req: InternalAxiosRequestConfig
  ): Promise<InternalAxiosRequestConfig> {
    const token = await this.storage.getAccessToken();

    if (token) {
      req.headers = req.headers ?? {};
      req.headers['Authorization'] = `Bearer ${token}`;
    }

    if (req.data instanceof FormData) {
      delete req.headers['Content-Type'];
    }

    return req;
  }

  private async responseErrorInterceptor(
    error: unknown
  ): Promise<never> {
    if (!axios.isAxiosError(error)) {
      throw error;
    }

    const status = error.response?.status;

    if (
      status === 401 &&
      !this.isRefreshRequest(error.config)
    ) {
      try {
        const tokens = await this.refreshTokens();

        const originalConfig =
          error.config as AxiosRequestConfig;

        if (originalConfig.headers) {
          originalConfig.headers['Authorization'] =
            `Bearer ${tokens.accessToken}`;
        }

        return this.http.request(originalConfig) as never;
      } catch {
        this.onUnauthorized?.();
        throw error;
      }
    }

    const serverMsg =
      error.response?.data?.error?.message ||
      error.response?.data?.message;

    if (serverMsg && typeof serverMsg === 'string') {
      error.message = serverMsg;
    }

    throw error;
  }

  private isRefreshRequest(
    config?: AxiosRequestConfig
  ): boolean {
    return config?.url?.includes('/auth/refresh') ?? false;
  }

  /** Deduplicated refresh — prevents concurrent refresh storms */
  async refreshTokens(): Promise<AuthTokens> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      const refreshToken =
        await this.storage.getRefreshToken();

      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await this.http.post<AuthTokens>(
        '/auth/refresh',
        {
          refreshToken,
        } satisfies RefreshRequest
      );

      await this.storage.setAccessToken(
        response.data.accessToken
      );

      await this.storage.setRefreshToken(
        response.data.refreshToken
      );

      return response.data;
    })().finally(() => {
      this.refreshPromise = null;
    });

    return this.refreshPromise;
  }

  async setTokens(tokens: AuthTokens): Promise<void> {
    await this.storage.setAccessToken(tokens.accessToken);
    await this.storage.setRefreshToken(tokens.refreshToken);
  }

  async clearTokens(): Promise<void> {
    await this.storage.clearTokens();
  }

  // ─── Auth endpoints ─────────────────────────────────────────────────────────

  auth = {
    register: (data: RegisterRequest) =>
      this.http
        .post<AuthResponse>('/auth/register', data)
        .then((r) => r.data),

    login: (data: LoginRequest) =>
      this.http
        .post<AuthResponse>('/auth/login', data)
        .then((r) => r.data),

    activate: (data: ActivateAccountRequest) =>
      this.http
        .post<AuthResponse>('/auth/activate', data)
        .then((r) => r.data),

    changePassword: (data: ChangePasswordRequest) =>
      this.http
        .post<void>('/auth/change-password', data)
        .then(() => undefined),

    logout: (data: LogoutRequest) =>
      this.http
        .post<void>('/auth/logout', data)
        .then(() => undefined),

    me: () =>
      this.http
        .get<User>('/auth/me')
        .then((r) => r.data),
  };

  // ─── Users ──────────────────────────────────────────────────────────────────

  users = {
    getMe: () =>
      this.http
        .get<User>('/users/me')
        .then((r) => r.data),

    updateMe: (data: UpdateUserProfileRequest) =>
      this.http
        .put<User>('/users/me', data)
        .then((r) => r.data),

    uploadAvatar: (file: File | Blob) => {
      const formData = new FormData();
      formData.append('file', file);
      return this.http
        .post<User>('/users/me/avatar', formData)
        .then((r) => r.data);
    },

    deleteAvatar: () =>
      this.http
        .delete<User>('/users/me/avatar')
        .then((r) => r.data),

    get: (userId: string) =>
      this.http
        .get<User>(`/users/${userId}`)
        .then((r) => r.data),
  };

  // ─── Invitations ────────────────────────────────────────────────────────────

  invitations = {
    accept: (data: AcceptInvitationRequest) =>
      this.http
        .post<Workspace>('/invitations/accept', data)
        .then((r) => r.data),

    preview: (token: string) =>
      this.http
        .get<WorkspaceInvitation>(
          '/invitations/preview',
          { params: { token } }
        )
        .then((r) => r.data),
  };

  // ─── Workspaces ─────────────────────────────────────────────────────────────

  workspaces = {
    create: (data: CreateWorkspaceRequest) =>
      this.http
        .post<Workspace>('/workspaces', data)
        .then((r) => r.data),

    list: () =>
      this.http
        .get<Workspace[]>('/workspaces')
        .then((r) => r.data),

    get: (workspaceId: string) =>
      this.http
        .get<Workspace>(`/workspaces/${workspaceId}`)
        .then((r) => r.data),

    update: (
      workspaceId: string,
      data: UpdateWorkspaceRequest
    ) =>
      this.http
        .put<Workspace>(
          `/workspaces/${workspaceId}`,
          data
        )
        .then((r) => r.data),

    delete: (workspaceId: string) =>
      this.http
        .delete<void>(`/workspaces/${workspaceId}`)
        .then(() => undefined),

    uploadIcon: (workspaceId: string, file: File | Blob) => {
      const formData = new FormData();
      formData.append('file', file);
      return this.http
        .post<Workspace>(`/workspaces/${workspaceId}/icon`, formData)
        .then((r) => r.data);
    },

    uploadEmployeePhoto: (workspaceId: string, file: File | Blob) => {
      const formData = new FormData();
      formData.append('file', file);
      return this.http
        .post<{ tempObjectKey: string }>(`/workspaces/${workspaceId}/employee-media`, formData)
        .then((r) => r.data);
    },

    deleteIcon: (workspaceId: string) =>
      this.http
        .delete<Workspace>(`/workspaces/${workspaceId}/icon`)
        .then((r) => r.data),

    listMembers: (workspaceId: string) =>
      this.http
        .get<WorkspaceMember[]>(
          `/workspaces/${workspaceId}/members`
        )
        .then((r) => r.data),

    removeMember: (
      workspaceId: string,
      userId: string
    ) =>
      this.http
        .delete<void>(
          `/workspaces/${workspaceId}/members/${userId}`
        )
        .then(() => undefined),

    invite: (
      workspaceId: string,
      data: CreateInvitationRequest
    ) =>
      this.http
        .post<WorkspaceInvitation>(
          `/workspaces/${workspaceId}/invitations`,
          data
        )
        .then((r) => r.data),
  };

  // ─── Groups ─────────────────────────────────────────────────────────────────

  groups = {
    create: (
      workspaceId: string,
      data: CreateGroupRequest
    ) =>
      this.http
        .post<Group>(
          `/workspaces/${workspaceId}/groups`,
          data
        )
        .then((r) => r.data),

    list: (workspaceId: string) =>
      this.http
        .get<Group[]>(
          `/workspaces/${workspaceId}/groups`
        )
        .then((r) => r.data),

    get: (groupId: string) =>
      this.http
        .get<Group>(`/groups/${groupId}`)
        .then((r) => r.data),

    update: (
      groupId: string,
      data: UpdateGroupRequest
    ) =>
      this.http
        .put<Group>(
          `/groups/${groupId}`,
          data
        )
        .then((r) => r.data),

    delete: (groupId: string) =>
      this.http
        .delete<void>(`/groups/${groupId}`)
        .then(() => undefined),

    addMember: (
      groupId: string,
      userId: string
    ) =>
      this.http
        .post<GroupMember>(
          `/groups/${groupId}/members`,
          { userId }
        )
        .then((r) => r.data),

    removeMember: (
      groupId: string,
      userId: string
    ) =>
      this.http
        .delete<void>(
          `/groups/${groupId}/members/${userId}`
        )
        .then(() => undefined),
  };

  // ─── Channels ───────────────────────────────────────────────────────────────

  channels = {
    create: (
      groupId: string,
      data: CreateChannelRequest
    ) =>
      this.http
        .post<Channel>(
          `/groups/${groupId}/channels`,
          data
        )
        .then((r) => r.data),

    listForWorkspace: (workspaceId: string) =>
      this.http
        .get<Channel[]>(
          `/workspaces/${workspaceId}/channels`
        )
        .then((r) => r.data),

    get: (channelId: string) =>
      this.http
        .get<Channel>(`/channels/${channelId}`)
        .then((r) => r.data),

    update: (
      channelId: string,
      data: UpdateChannelRequest
    ) =>
      this.http
        .put<Channel>(
          `/channels/${channelId}`,
          data
        )
        .then((r) => r.data),

    delete: (channelId: string) =>
      this.http
        .delete<void>(`/channels/${channelId}`)
        .then(() => undefined),

    addMember: (
      channelId: string,
      userId: string
    ) =>
      this.http
        .post<ChannelMember>(
          `/channels/${channelId}/members`,
          { userId }
        )
        .then((r) => r.data),

    removeMember: (
      channelId: string,
      userId: string
    ) =>
      this.http
        .delete<void>(
          `/channels/${channelId}/members/${userId}`
        )
        .then(() => undefined),
  };

  // ─── Messages ───────────────────────────────────────────────────────────────

  messages = {
    list: (
      channelId: string,
      params?: {
        cursor?: string;
        after?: string;
        limit?: number;
      }
    ) =>
      this.http
        .get<PaginatedResponse<Message>>(
          `/channels/${channelId}/messages`,
          { params }
        )
        .then((r) => r.data),

    send: (
      channelId: string,
      data: SendMessageRequest
    ) =>
      this.http
        .post<Message>(
          `/channels/${channelId}/messages`,
          data
        )
        .then((r) => r.data),

    update: (
      messageId: string,
      data: UpdateMessageRequest
    ) =>
      this.http
        .put<Message>(
          `/messages/${messageId}`,
          data
        )
        .then((r) => r.data),

    delete: (messageId: string) =>
      this.http
        .delete<void>(`/messages/${messageId}`)
        .then(() => undefined),

    addReaction: (
      messageId: string,
      emoji: string
    ) =>
      this.http
        .post<void>(
          `/messages/${messageId}/reactions`,
          { emoji }
        )
        .then(() => undefined),

    removeReaction: (
      messageId: string,
      emoji: string
    ) =>
      this.http
        .delete<void>(
          `/messages/${messageId}/reactions/${encodeURIComponent(
            emoji
          )}`
        )
        .then(() => undefined),

    listReplies: (messageId: string) =>
      this.http
        .get<Message[]>(
          `/messages/${messageId}/replies`
        )
        .then((r) => r.data),

    reply: (
      messageId: string,
      data: SendMessageRequest
    ) =>
      this.http
        .post<Message>(
          `/messages/${messageId}/replies`,
          data
        )
        .then((r) => r.data),
  };

  // ─── Direct conversations ────────────────────────────────────────────────────

  directConversations = {
    list: (workspaceId: string) =>
      this.http
        .get<DirectConversation[]>(
          `/workspaces/${workspaceId}/direct-conversations`
        )
        .then((r) => r.data),

    create: (
      workspaceId: string,
      data: CreateDirectConversationRequest
    ) =>
      this.http
        .post<DirectConversation>(
          `/workspaces/${workspaceId}/direct-conversations`,
          data
        )
        .then((r) => r.data),

    get: (conversationId: string) =>
      this.http
        .get<DirectConversation>(
          `/direct-conversations/${conversationId}`
        )
        .then((r) => r.data),

    listMessages: (
      conversationId: string,
      params?: {
        cursor?: string;
        after?: string;
        limit?: number;
      }
    ) =>
      this.http
        .get<PaginatedResponse<DirectMessage>>(
          `/direct-conversations/${conversationId}/messages`,
          { params }
        )
        .then((r) => r.data),

    sendMessage: (
      conversationId: string,
      data: SendDirectMessageRequest
    ) =>
      this.http
        .post<DirectMessage>(
          `/direct-conversations/${conversationId}/messages`,
          data
        )
        .then((r) => r.data),

    updateMessage: (
      messageId: string,
      data: UpdateMessageRequest
    ) =>
      this.http
        .put<DirectMessage>(
          `/direct-messages/${messageId}`,
          data
        )
        .then((r) => r.data),

    deleteMessage: (messageId: string) =>
      this.http
        .delete<void>(
          `/direct-messages/${messageId}`
        )
        .then(() => undefined),

    markRead: (conversationId: string) =>
      this.http
        .put<void>(
          `/direct-conversations/${conversationId}/read`
        )
        .then(() => undefined),

    addReaction: (
      messageId: string,
      emoji: string
    ) =>
      this.http
        .post<void>(
          `/direct-messages/${messageId}/reactions`,
          { emoji }
        )
        .then(() => undefined),

    removeReaction: (
      messageId: string,
      emoji: string
    ) =>
      this.http
        .delete<void>(
          `/direct-messages/${messageId}/reactions/${encodeURIComponent(
            emoji
          )}`
        )
        .then(() => undefined),
  };

  // ─── Notifications ───────────────────────────────────────────────────────────

  notifications = {
    list: (
      workspaceId: string,
      query?: NotificationFilterQuery
    ) =>
      this.http
        .get<PaginatedResponse<Notification>>(
          '/notifications',
          {
            params: {
              workspaceId,
              type: query?.type,
              unreadOnly: query?.unreadOnly,
              cursor: query?.cursor,
              limit: query?.limit,
            },
          }
        )
        .then((r) => r.data),

    markRead: (notificationId: string) =>
      this.http
        .put<void>(
          `/notifications/${notificationId}/read`
        )
        .then(() => undefined),

    markAllRead: (workspaceId: string) =>
      this.http
        .put<void>(
          '/notifications/read-all',
          undefined,
          {
            params: { workspaceId },
          }
        )
        .then(() => undefined),

    unreadCount: (workspaceId: string) =>
      this.http
        .get<{ count: number }>(
          '/notifications/unread-count',
          {
            params: { workspaceId },
          }
        )
        .then((r) => r.data.count),

    getPreferences: (workspaceId: string) =>
      this.http
        .get<UserNotificationPreferences>(
          '/notifications/preferences',
          {
            params: { workspaceId },
          }
        )
        .then((r) => r.data),

    updatePreferences: (workspaceId: string, data: UpdateNotificationPreferencesRequest) =>
      this.http
        .put<UserNotificationPreferences>(
          '/notifications/preferences',
          data,
          {
            params: { workspaceId },
          }
        )
        .then((r) => r.data),

    getChannelSetting: (channelId: string) =>
      this.http
        .get<ChannelNotificationSetting>(
          `/notifications/channels/${channelId}/setting`
        )
        .then((r) => r.data),

    updateChannelSetting: (channelId: string, data: UpdateChannelNotificationSettingRequest) =>
      this.http
        .put<ChannelNotificationSetting>(
          `/notifications/channels/${channelId}/setting`,
          data
        )
        .then((r) => r.data),

    followThread: (messageId: string) =>
      this.http
        .post<void>(
          `/notifications/threads/${messageId}/follow`
        )
        .then(() => undefined),

    unfollowThread: (messageId: string) =>
      this.http
        .delete<void>(
          `/notifications/threads/${messageId}/follow`
        )
        .then(() => undefined),
  };

  // ─── Files ───────────────────────────────────────────────────────────────────

  files = {
    upload: (
      workspaceId: string,
      file: File,
      options?: {
        channelId?: string;
        conversationId?: string;
      }
    ) => {
      const formData = new FormData();
      formData.append('file', file);
      if (options?.channelId) {
        formData.append('channelId', options.channelId);
      }
      if (options?.conversationId) {
        formData.append('conversationId', options.conversationId);
      }

      return this.http
        .post<WorkspaceFile>(
          `/workspaces/${workspaceId}/files/upload`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            timeout: 120_000,
          }
        )
        .then((r) => r.data);
    },

    list: (
      workspaceId: string,
      params?: {
        channelId?: string;
        conversationId?: string;
        filter?: string;
        fileType?: string;
        category?: string;
        query?: string;
        page?: number;
        size?: number;
      }
    ) =>
      this.http
        .get<PaginatedResponse<WorkspaceFile>>(
          `/workspaces/${workspaceId}/files`,
          { params }
        )
        .then((r) => r.data),

    getPresignedDownloadUrl: (workspaceId: string, fileId: string) =>
      this.http
        .get<{
          downloadUrl?: string;
          url?: string;
          fileName: string;
          contentType: string;
          fileSize: number;
          expiresAt: string;
        }>(`/workspaces/${workspaceId}/files/${fileId}/presigned-download`)
        .then((r) => r.data),

    getDownloadUrl: (workspaceId: string, fileId: string, token?: string) => {
      const tokenQuery = token ? `?token=${encodeURIComponent(token)}` : '';
      return `/api/v1/workspaces/${workspaceId}/files/${fileId}/download${tokenQuery}`;
    },

    delete: (
      workspaceId: string,
      fileId: string
    ) =>
      this.http
        .delete<void>(
          `/workspaces/${workspaceId}/files/${fileId}`
        )
        .then(() => undefined),
  };

  // ─── Devices ─────────────────────────────────────────────────────────────────

  devices = {
    register: (data: RegisterDeviceRequest) =>
      this.http
        .post<Device>('/devices', data)
        .then((r) => r.data),

    deregister: (deviceId: string) =>
      this.http
        .delete<void>(`/devices/${deviceId}`)
        .then(() => undefined),

    updateToken: (
      deviceId: string,
      data: UpdateDeviceTokenRequest
    ) =>
      this.http
        .put<Device>(
          `/devices/${deviceId}/token`,
          data
        )
        .then((r) => r.data),
  };

  // ─── Group Calls ─────────────────────────────────────────────────────────────

  groupCalls = {
    getActiveChannelCall: (
      workspaceId: string,
      channelId: string
    ) =>
      this.http
        .get<any | null>(
          `/workspaces/${workspaceId}/channels/${channelId}/call`
        )
        .then((r) => r.data || null),

    getIceServers: (workspaceId: string) =>
      this.http
        .get<any[]>(
          `/workspaces/${workspaceId}/ice-servers`
        )
        .then((r) => r.data || []),
  };

  // ─── Emergency Alerts ────────────────────────────────────────────────────────

  emergencyAlerts = {
    create: (
      workspaceId: string,
      data: CreateEmergencyAlertRequest
    ) =>
      this.http
        .post<EmergencyAlertDto>(
          `/workspaces/${workspaceId}/emergency-alerts`,
          data
        )
        .then((r) => r.data),

    acknowledge: (
      workspaceId: string,
      alertId: string
    ) =>
      this.http
        .post<void>(
          `/workspaces/${workspaceId}/emergency-alerts/${alertId}/acknowledge`
        )
        .then(() => undefined),

    getActive: (workspaceId: string) =>
      this.http
        .get<EmergencyAlertDto[]>(
          `/workspaces/${workspaceId}/emergency-alerts/active`
        )
        .then((r) => r.data || []),
  };

  // ─── Platform ────────────────────────────────────────────────────────────────

  platform = {
    getDashboardStats: () =>
      this.http
        .get<DashboardStatsDto>(
          '/platform/dashboard'
        )
        .then((r) => r.data),

    getWorkspaces: (
      page: number = 0,
      size: number = 20
    ) =>
      this.http
        .get<PageResponse<Workspace>>(
          '/platform/workspaces',
          {
            params: {
              page,
              size,
            },
          }
        )
        .then((r) => r.data),

    updateWorkspaceStatus: (
      workspaceId: string,
      status: WorkspaceStatus
    ) =>
      this.http
        .patch<Workspace>(
          `/platform/workspaces/${workspaceId}/status`,
          { status }
        )
        .then((r) => r.data),

    getUsers: (
      page: number = 0,
      size: number = 20
    ) =>
      this.http
        .get<PageResponse<PlatformUser>>(
          '/platform/users',
          {
            params: {
              page,
              size,
            },
          }
        )
        .then((r) => r.data),

    getAuditLogs: (
      page: number = 0,
      size: number = 20
    ) =>
      this.http
        .get<PageResponse<PlatformAuditLogDto>>(
          '/platform/audit-logs',
          {
            params: {
              page,
              size,
            },
          }
        )
        .then((r) => r.data),
  };

  // ─── Meetings ────────────────────────────────────────────────────────────────

  meetings = {
    create: (workspaceId: string, data: CreateMeetingRequest) =>
      this.http
        .post<Meeting>(`/workspaces/${workspaceId}/meetings`, data)
        .then((r) => r.data),

    list: (workspaceId: string, status?: string, page: number = 0, size: number = 20) =>
      this.http
        .get<PaginatedResponse<Meeting>>(`/workspaces/${workspaceId}/meetings`, {
          params: { status, page, size },
        })
        .then((r) => r.data),

    getStatusCounts: (workspaceId: string) =>
      this.http
        .get<MeetingStatusCounts>(`/workspaces/${workspaceId}/meetings/status-counts`)
        .then((r) => r.data),

    getDetails: (workspaceId: string, meetingId: string) =>
      this.http
        .get<Meeting>(`/workspaces/${workspaceId}/meetings/${meetingId}`)
        .then((r) => r.data),

    update: (workspaceId: string, meetingId: string, data: UpdateMeetingRequest) =>
      this.http
        .patch<Meeting>(`/workspaces/${workspaceId}/meetings/${meetingId}`, data)
        .then((r) => r.data),

    start: (workspaceId: string, meetingId: string) =>
      this.http
        .post<Meeting>(`/workspaces/${workspaceId}/meetings/${meetingId}/start`)
        .then((r) => r.data),

    cancel: (workspaceId: string, meetingId: string) =>
      this.http
        .post<Meeting>(`/workspaces/${workspaceId}/meetings/${meetingId}/cancel`)
        .then((r) => r.data),

    end: (workspaceId: string, meetingId: string) =>
      this.http
        .post<Meeting>(`/workspaces/${workspaceId}/meetings/${meetingId}/end`)
        .then((r) => r.data),

    addParticipant: (workspaceId: string, meetingId: string, data: AddParticipantRequest) =>
      this.http
        .post<MeetingParticipant>(`/workspaces/${workspaceId}/meetings/${meetingId}/participants`, data)
        .then((r) => r.data),

    removeParticipant: (workspaceId: string, meetingId: string, participantId: string) =>
      this.http
        .delete<void>(`/workspaces/${workspaceId}/meetings/${meetingId}/participants/${participantId}`)
        .then(() => undefined),

    createInvitation: (workspaceId: string, meetingId: string, data: CreateMeetingInvitationRequest) =>
      this.http
        .post<MeetingInvitation>(`/workspaces/${workspaceId}/meetings/${meetingId}/invitations`, data)
        .then((r) => r.data),

    revokeInvitation: (workspaceId: string, meetingId: string, invitationId: string) =>
      this.http
        .post<void>(`/workspaces/${workspaceId}/meetings/${meetingId}/invitations/${invitationId}/revoke`)
        .then(() => undefined),

    validateJoinToken: (token: string) =>
      this.http
        .get<MeetingJoinTokenResponse>(`/meetings/join/${token}`)
        .then((r) => r.data),

    joinAsGuest: (token: string, data: GuestJoinRequest) =>
      this.http
        .post<GuestJoinResponse>(`/meetings/join/${token}/guest`, data)
        .then((r) => r.data),
  };

  // ─── Translation ─────────────────────────────────────────────────────────────

  translation = {
    translate: (data: TranslateRequest) =>
      this.http
        .post<TranslateResponse>('/translate', data)
        .then((r) => r.data),
  };

  // ─── Workforce Activity ───────────────────────────────────────────────────────

  workforce = {
    getLiveState: (workspaceId: string) =>
      this.http
        .get<any[]>(`/workforce/live-state`, { params: { workspaceId } })
        .then((r) => r.data),

    getTimeline: (workspaceId: string, userId: string) =>
      this.http
        .get<any[]>(`/workforce/timeline`, { params: { workspaceId, userId } })
        .then((r) => r.data),

    getEmployees: (workspaceId?: string, search?: string, status?: string, date?: string) =>
      this.http
        .get<any[]>(`/workforce/employees`, { params: { workspaceId, search, status, date } })
        .then((r) => r.data),

    getEmployeeActivity: (userId: string, workspaceId: string, date?: string) =>
      this.http
        .get<any>(`/workforce/employees/${userId}/activity`, { params: { workspaceId, date } })
        .then((r) => r.data),
  };
}

export { type AxiosInstance };